import {Injectable} from "@angular/core";

import {ApiService} from "app/services/domain/api.service";
import {UsersService} from "app/services/domain/users.service";
import * as Models from "app/services/proxy/model/models";
import {PolicyUtilsService} from "app/policy/policy-utils.service";
import {FeatureFlagsService} from "app/services/third-party/feature-flags/feature-flags.service";
import {FeatureFlagsRequester} from "app/services/third-party/feature-flags/feature-flags-requester.service";

export interface PolicyScopeConfigs
{
    account?: Models.NamedParameter[];
    tenant?: Models.NamedParameter[];
    master?: Models.NamedParameter[];
}

@Injectable()
export class PolicyParameterConfigService
{
    constructor(private api: ApiService,
                private featureFlags: FeatureFlagsService,
                private featureFlagsRequester: FeatureFlagsRequester,
                private users: UsersService)
    {}

    public registerMasterAccountConfigFlag(): Promise<void>
    {
        return this.featureFlagsRequester.registerFeatureFlagRequest(Models.FeatureFlagCategory.FEATURE,
                                                                     Models.HeatstackFeatureFlags.heat_2602_a_master_account_policy_config);
    }

    public isMasterAccountConfigEnabled(): boolean
    {
        return this.featureFlags.featureFlagsState()[Models.HeatstackFeatureFlags.heat_2602_a_master_account_policy_config]?.enabled ?? false;
    }

    public async getNamedParameters(policyUri: string,
                                    tenantId: string): Promise<Models.NamedParameter[]>
    {
        if (!policyUri)
        {
            return [];
        }
        const request      = Models.PolicyDescriptionRequest.newInstance({policy_uris: [policyUri]});
        const descriptions = await this.api.guardrails.policiesBatch(tenantId, request);
        return descriptions?.length ? (descriptions[0].parameters || []) : [];
    }

    public async getTenantConfig(policyId: string,
                                 tenantId: string): Promise<Models.NamedParameter[]>
    {
        try
        {
            const config = await this.api.guardrails.getTenantPolicyConfiguration(policyId, tenantId);
            return config?.parameters_configuration || [];
        }
        catch
        {
            return [];
        }
    }

    public async getMasterAccountConfig(policyId: string): Promise<Models.NamedParameter[]>
    {
        if (!this.isMasterAccountConfigEnabled() || !this.users.masterAccountId)
        {
            return [];
        }
        try
        {
            const config = await this.api.guardrails.getMasterAccountPolicyConfiguration(this.users.masterAccountId, policyId);
            return config?.parameters_configuration || [];
        }
        catch
        {
            return [];
        }
    }

    public async getAccountConfig(policyId: string,
                                  accountId: string): Promise<Models.NamedParameter[]>
    {
        if (!accountId)
        {
            return [];
        }
        try
        {
            const config = await this.api.guardrails.getServiceAccountPolicyConfiguration(policyId, accountId);
            return config?.parameters_configuration || [];
        }
        catch
        {
            return [];
        }
    }

    public resolveParameterValues(namedParameters: Models.NamedParameter[],
                                  configs: PolicyScopeConfigs): Record<string, any>
    {
        const resolved: Record<string, any> = {};
        const knownNames                     = new Set(namedParameters.map(p => p.name));

        for (const param of namedParameters)
        {
            if (param.schema?.default_value != null)
            {
                resolved[param.name] = PolicyUtilsService.parseDefaultValue(param.schema.default_value);
            }
        }
        for (const config of [configs.master, configs.tenant, configs.account])
        {
            for (const param of config ?? [])
            {
                const value = param.schema?.default_value;
                if (knownNames.has(param.name) && value != null)
                {
                    resolved[param.name] = PolicyUtilsService.parseDefaultValue(value);
                }
            }
        }

        return resolved;
    }

    public async loadResolvedParameterValues(policyId: string,
                                             tenantId: string,
                                             accountId: string,
                                             namedParameters: Models.NamedParameter[]): Promise<Record<string, any>>
    {
        await this.registerMasterAccountConfigFlag();
        const [account, tenant, master] = await Promise.allSettled([
                                                                       this.getAccountConfig(policyId, accountId),
                                                                       this.getTenantConfig(policyId, tenantId),
                                                                       this.getMasterAccountConfig(policyId)
                                                                   ]);
        return this.resolveParameterValues(namedParameters,
                                           {
                                               account: this.settledConfig(account),
                                               tenant : this.settledConfig(tenant),
                                               master : this.settledConfig(master)
                                           });
    }

    private settledConfig(result: PromiseSettledResult<Models.NamedParameter[]>): Models.NamedParameter[]
    {
        return result.status === "fulfilled" ? result.value : [];
    }
}
